/**
 * CommunityPulse — compact Dashboard widget that surfaces the newest forum
 * activity so the community feels alive from the moment users log in.
 *
 * Layout: single card with three tight sections:
 *   • Announcement highlight (if any)
 *   • Latest topics (up to 3)
 *   • Next upcoming event (if any) + bookmark count + 24h buzz counter
 * All roads lead back to /community.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { formatDistanceToNow } from 'date-fns';
import {
  Users, MessageSquare, Megaphone, Calendar, ArrowRight, Flame,
  Bookmark, Sparkles,
} from 'lucide-react';

const rel = (iso) => {
  if (!iso) return '';
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return ''; }
};

const CommunityPulse = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [pulse, setPulse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/forum/pulse`);
        setPulse(r.data);
      } catch { setPulse(null); }
      finally { setLoading(false); }
    })();
  }, [API]);

  if (loading) {
    return (
      <div className="pro-card p-4" data-testid="community-pulse-loading">
        <div className="h-4 w-32 bg-muted rounded animate-pulse mb-3" />
        <div className="space-y-2">
          <div className="h-3 bg-muted rounded animate-pulse" />
          <div className="h-3 bg-muted rounded animate-pulse w-4/5" />
        </div>
      </div>
    );
  }

  const openTopic = (t) => {
    if (t.lesson_id) navigate(`/lesson/${t.lesson_id}`);
    else navigate(`/community/t/${t.id}`);
  };

  const nothingHere = !pulse || (
    (!pulse.latest || pulse.latest.length === 0) && !pulse.announcement && !pulse.next_event
  );

  if (nothingHere) {
    return (
      <div className="pro-card p-5" data-testid="community-pulse">
        <div className="flex items-center gap-2 mb-2">
          <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
            <Users className="w-4 h-4" />
          </div>
          <div>
            <div className="eyebrow text-muted-foreground">Community</div>
            <div className="font-heading font-extrabold text-foreground">Say hi to the forum</div>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          It&apos;s quiet in here. Introduce yourself, ask a question, or share what you&apos;re drying today.
        </p>
        <Link
          to="/community"
          className="inline-flex items-center gap-1 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-bold px-3 h-8 transition-colors"
          data-testid="community-pulse-open"
        >
          Open community <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    );
  }

  const evt = pulse.next_event;
  const evtDate = evt?.event_starts_at ? new Date(evt.event_starts_at) : null;

  return (
    <div className="pro-card p-4 space-y-3" data-testid="community-pulse">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
            <Users className="w-4 h-4" />
          </div>
          <div>
            <div className="eyebrow text-muted-foreground">Community pulse</div>
            <div className="font-heading font-extrabold text-foreground text-sm">What&apos;s happening now</div>
          </div>
        </div>
        {pulse.buzz_24h > 0 && (
          <div className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 text-amber-700 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-widest" data-testid="community-pulse-buzz">
            <Flame className="w-3 h-3" /> {pulse.buzz_24h} · 24h
          </div>
        )}
      </div>

      {/* Announcement */}
      {pulse.announcement && (
        <button
          type="button"
          onClick={() => openTopic(pulse.announcement)}
          data-testid="community-pulse-announcement"
          className="w-full text-left rounded-lg border border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10 px-3 py-2 transition-colors"
        >
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-amber-700">
            <Megaphone className="w-3 h-3" /> Announcement
          </div>
          <div className="mt-0.5 text-sm font-bold text-foreground line-clamp-2">{pulse.announcement.title}</div>
        </button>
      )}

      {/* Latest topics */}
      {pulse.latest?.length > 0 && (
        <div className="space-y-1.5" data-testid="community-pulse-latest">
          {pulse.latest.map((t) => {
            const color = t.category?.color || '#0EA5E9';
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => openTopic(t)}
                data-testid={`community-pulse-topic-${t.id}`}
                className="w-full text-left rounded-lg hover:bg-muted px-2.5 py-2 transition-colors"
              >
                <div className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-foreground truncate">{t.title}</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {t.category?.name} · {t.author?.name} · {rel(t.last_activity_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-[10px] font-bold text-muted-foreground flex-shrink-0">
                    <MessageSquare className="w-3 h-3" /> {t.reply_count || 0}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Next event */}
      {evt && evtDate && (
        <button
          type="button"
          onClick={() => openTopic(evt)}
          data-testid="community-pulse-event"
          className="w-full text-left rounded-lg border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10 px-3 py-2 transition-colors flex items-center gap-3"
        >
          <div className="flex flex-col items-center justify-center rounded-md bg-emerald-500/15 text-emerald-700 px-2 py-1 flex-shrink-0 min-w-[44px]">
            <span className="text-[9px] uppercase font-bold tracking-widest leading-none">
              {evtDate.toLocaleDateString('en-US', { month: 'short' })}
            </span>
            <span className="text-sm font-extrabold leading-none">{evtDate.getDate()}</span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-emerald-700">
              <Calendar className="w-3 h-3" /> Next event
            </div>
            <div className="text-xs font-bold text-foreground truncate">{evt.title}</div>
          </div>
        </button>
      )}

      {/* Footer CTAs */}
      <div className="flex items-center justify-between pt-1 border-t border-border">
        <Link
          to="/community?tab=bookmarks"
          className="inline-flex items-center gap-1 text-[11px] font-bold text-muted-foreground hover:text-foreground"
        >
          <Bookmark className="w-3 h-3" /> {pulse.my_bookmarks || 0} bookmarked
        </Link>
        <Link
          to="/community"
          className="inline-flex items-center gap-1 text-[11px] font-bold text-primary hover:underline"
          data-testid="community-pulse-open"
        >
          Open community <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
};

export default CommunityPulse;
